import React, {useState, useEffect} from 'react';
import {DashboardLayout, Loader} from '../../components';
import {PersonalInfoForm, PersonalInfoFormData} from './personal-info-form';
import {CompanyDetailsForm, CompanyDetailsFormData} from './company-details-form';
import {useSettingsData} from './use-settings-data';
import {useAuth} from '../../contexts/auth.provider';
import {toast} from 'react-toastify';

enum TabType {
  PERSONAL_INFO = 'personal-info',
  COMPANY_DETAILS = 'company-details',
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>(TabType.PERSONAL_INFO);
  const [personalInfoInitialValues, setPersonalInfoInitialValues] = useState<PersonalInfoFormData | null>(null);
  const [companyDetailsInitialValues, setCompanyDetailsInitialValues] = useState<CompanyDetailsFormData | null>(null);
  const {fetchAccountDetails, account} = useAuth();
  const {error, getPersonalInfoInitialValues, getCompanyDetailsInitialValues, savePersonalInfo, saveCompanyDetails} = useSettingsData();

  useEffect(() => {
    if (!account) {
      fetchAccountDetails();
    }
  }, [account, fetchAccountDetails]);

  useEffect(() => {
    const loadPersonalInfoInitialValues = async () => {
      try {
        const initialValues = await getPersonalInfoInitialValues();
        setPersonalInfoInitialValues(initialValues);
      } catch (personalInfoError: any) {
        toast.error('Failed to load personal information');
      }
    };

    if (account) {
      loadPersonalInfoInitialValues();
    }
  }, [account, getPersonalInfoInitialValues]);

  useEffect(() => {
    const loadCompanyDetailsInitialValues = async () => {
      try {
        const initialValues = await getCompanyDetailsInitialValues();
        setCompanyDetailsInitialValues(initialValues);
      } catch (companyDetailsError: any) {
        toast.error('Failed to load company details');
      }
    };

    if (account) {
      loadCompanyDetailsInitialValues();
    }
  }, [account, getCompanyDetailsInitialValues]);

  const handleCancel = (resetForm?: () => void) => {
    if (resetForm) {
      resetForm();
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        </div>

        <div className="mb-8">
          <div className="inline-flex bg-white rounded-full p-1">
            <button
              onClick={() => setActiveTab(TabType.PERSONAL_INFO)}
              className={`px-6 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                activeTab === TabType.PERSONAL_INFO ? 'bg-blue-500 text-white shadow-md' : 'text-gray-700 hover:text-gray-900'
              }`}>
              Personal Info
            </button>
            <button
              onClick={() => setActiveTab(TabType.COMPANY_DETAILS)}
              className={`px-6 py-3 text-sm font-medium rounded-full transition-all duration-200 ${
                activeTab === TabType.COMPANY_DETAILS ? 'bg-blue-500 text-white shadow-md' : 'text-gray-700 hover:text-gray-900'
              }`}>
              Company Details
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-8">
          {activeTab === TabType.PERSONAL_INFO &&
            (personalInfoInitialValues ? (
              <PersonalInfoForm initialValues={personalInfoInitialValues} onSubmit={savePersonalInfo} onCancel={handleCancel} />
            ) : (
              <div className="flex justify-center items-center py-8">
                <Loader />
              </div>
            ))}

          {activeTab === TabType.COMPANY_DETAILS &&
            (companyDetailsInitialValues ? (
              <CompanyDetailsForm initialValues={companyDetailsInitialValues} onSubmit={saveCompanyDetails} onCancel={handleCancel} />
            ) : (
              <div className="flex justify-center items-center py-8">
                <Loader />
              </div>
            ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
